import {
  ProductSaleLiteModel,
  useGetProductsSale,
} from 'api-hooks/product/sales';
import { NavigationRoutes } from 'common/constants';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody, DrawerFooter } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import PaginationComponent from 'components/elements/pagination-component';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useDrawer from 'hooks/use-drawer';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { CustomerListTagname } from 'legacy-modules/sales/component/list-tagname';
import {
  FullContainer,
  SeparatorVertical,
  TableContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  isShowPagination?: boolean;
  id: string;
}

export default function ProductSalesDetailTable(props: Props) {
  const { isShowPagination = false } = props;

  const { t } = useTranslation();
  const formatDate = useDate();

  const product_id = props.id;

  const [page, setPage] = React.useState(1);

  const permission = useGetCrudPermission('product-sales');

  const rowLink = React.useCallback(
    (row) => {
      if (!permission.view) return undefined;
      return {
        pathname: NavigationRoutes.ProductSalesView,
        query: {
          id: row.original.id,
        },
      };
    },
    [permission.view],
  );

  const _columns = React.useMemo<IColumn<ProductSaleLiteModel>[]>(
    () => [
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        stickyLeft: true,
        minSize: 150,
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 250,
        cell: ({ row }) => {
          const customer = row.original.customer;
          return <CustomerListTagname customer={customer} />;
        },
      },
      {
        header: t('document:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      // {
      //   header: t('common:variant'),
      //   accessorKey: 'variant',
      //   minSize: 250,
      //   sortName: 'transaction_at',
      // },
    ],
    [formatDate, t],
  );

  const {
    data,
    // refetch,
    isLoading,
    isFetching,
    error,
    extras: [, { columns }],
  } = useComposedQuery(
    useGetProductsSale,
    {
      params: {
        page,
        filter: {
          product_id,
          is_valid: true,
        },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const drawer = useDrawer();
  const onViewMore = React.useCallback(() => {
    drawer.showCustom({
      backdropClose: true,
      render: (close) => (
        <>
          <DrawerHeader onClose={close} title={t('common:sales_details')} />
          <DrawerBody>
            <TableContainer>
              <TableComponent
                columns={columns}
                isLoading={isLoading || isFetching}
                data={data?.data || []}
                rowLink={rowLink}
              />
            </TableContainer>
          </DrawerBody>
          <DrawerFooter $style={{ justifyContent: 'center' }}>
            <PaginationComponent
              page={page}
              onPageChange={(page) => setPage(page)}
              meta={data?.meta}
            />
          </DrawerFooter>
        </>
      ),
    });
  }, [
    columns,
    data?.data,
    data?.meta,
    drawer,
    isFetching,
    isLoading,
    page,
    rowLink,
    t,
  ]);

  return (
    <>
      <FullContainer
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text variant="HeadingXSmall">{t('common:sales_details')}</Text>
        <Button buttonType="secondary" onClick={onViewMore}>
          {t('common:view_more')}
        </Button>
      </FullContainer>
      <SeparatorVertical $margin={24} />
      <TableContainer $style={{ minHeight: '200px' }}>
        <TableComponent
          columns={columns}
          isLoading={isLoading || isFetching}
          data={data?.data || []}
          rowLink={rowLink}
        />
      </TableContainer>
      {isShowPagination && (
        <PaginationComponent
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
        />
      )}
    </>
  );
}
